import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { updateNote } from './notesSlice'

// 单个笔记编辑器的状态
interface NoteEditorState {
  noteId: string
  content: string
  title: string
  isEditing: boolean
  isSaving: boolean
  lastSavedAt: number | null
  selection: {
    from: number
    to: number
  } | null
  editorInstance: any // Tiptap编辑器实例引用
  isActive: boolean
  // 编辑器配置和状态
  editorConfig: {
    isFocused: boolean
    scrollPosition: number
    viewportState: any
  }
}

// 普通模式编辑器状态
export interface StandardNoteEditorState {
  // 所有打开的笔记编辑器状态，按noteId索引
  editors: Record<string, NoteEditorState>
  // 当前激活的笔记ID
  activeNoteId: string | null
  // 编辑器实例缓存，避免重复创建
  editorInstances: Record<string, any>
  // 当前布局模式
  layoutMode: 'tab' | 'split-horizontal' | 'split-vertical' | 'grid'
  // 自动保存配置
  autoSave: {
    enabled: boolean
    interval: number // 自动保存间隔（毫秒）
  }
  // 编辑器实例管理
  instanceManagement: {
    // 最大同时打开的编辑器数量（性能优化）
    maxInstances: number
    // 编辑器实例生命周期管理
    instanceLifetimes: Record<string, number>
    // 最近使用的编辑器ID（用于LRU缓存策略）
    recentlyUsed: string[]
  }
  // 状态过渡跟踪
  stateTransition: {
    // 状态加载中
    isLoadingState: boolean
    // 状态保存中
    isSavingState: boolean
    // 最后保存时间
    lastSavedAt: number | null
    // 最后加载时间
    lastLoadedAt: number | null
  }
}

const initialState: StandardNoteEditorState = {
  editors: {},
  activeNoteId: null,
  editorInstances: {},
  layoutMode: 'tab', // 默认布局为单页模式
  autoSave: {
    enabled: true,
    interval: 3000 // 3秒自动保存
  },
  instanceManagement: {
    maxInstances: 6, // 最多同时保持6个编辑器实例
    instanceLifetimes: {},
    recentlyUsed: []
  },
  stateTransition: {
    isLoadingState: false,
    isSavingState: false,
    lastSavedAt: null,
    lastLoadedAt: null
  }
}

const standardNoteEditorSlice = createSlice({
  name: 'standardNoteEditor',
  initialState,
  reducers: {
    // 打开笔记编辑器
    openNoteEditor: (state, action: PayloadAction<{ noteId: string; content: string; title: string }>) => {
      const { noteId, content } = action.payload
      
      // 如果编辑器已存在，只激活它
      if (state.editors[noteId]) {
        state.activeNoteId = noteId
        state.editors[noteId].isActive = true
        // 更新最近使用记录
        state.instanceManagement.recentlyUsed = state.instanceManagement.recentlyUsed
          .filter(id => id !== noteId)
          .concat(noteId)
        return
      }
      
      // 检查是否超过最大实例数，如果是则清理最不常用的实例
      if (Object.keys(state.editors).length >= state.instanceManagement.maxInstances) {
        const lruNoteId = state.instanceManagement.recentlyUsed[0]
        if (lruNoteId && lruNoteId !== noteId) {
          delete state.editors[lruNoteId]
          delete state.editorInstances[lruNoteId]
          delete state.instanceManagement.instanceLifetimes[lruNoteId]
          state.instanceManagement.recentlyUsed = state.instanceManagement.recentlyUsed
            .filter(id => id !== lruNoteId)
        }
      }
      
      // 创建新的编辑器状态
      state.editors[noteId] = {
        noteId,
        content,
        title: action.payload.title,
        isEditing: false,
        isSaving: false,
        lastSavedAt: null,
        selection: null,
        editorInstance: null,
        isActive: true,
        editorConfig: {
          isFocused: false,
          scrollPosition: 0,
          viewportState: null
        }
      }
      
      state.activeNoteId = noteId
      state.instanceManagement.recentlyUsed.push(noteId)
      state.instanceManagement.instanceLifetimes[noteId] = Date.now()
    },
    
    // 关闭笔记编辑器
    closeNoteEditor: (state, action: PayloadAction<string>) => {
      const noteId = action.payload
      
      // 移除编辑器状态
      delete state.editors[noteId]
      delete state.editorInstances[noteId]
      
      // 清理实例管理记录
      delete state.instanceManagement.instanceLifetimes[noteId]
      state.instanceManagement.recentlyUsed = state.instanceManagement.recentlyUsed
        .filter(id => id !== noteId)
      
      // 如果关闭的是当前激活的编辑器，激活相邻编辑器（相邻标签优先）
      if (state.activeNoteId === noteId) {
        const remaining = state.instanceManagement.recentlyUsed
        if (remaining.length > 0) {
          state.activeNoteId = remaining[remaining.length - 1]
          state.editors[state.activeNoteId].isActive = true
        } else {
          state.activeNoteId = null
        }
      }
    },
    
    // 激活笔记编辑器
    activateNoteEditor: (state, action: PayloadAction<string>) => {
      const noteId = action.payload
      
      // 将所有编辑器设为非激活状态
      Object.keys(state.editors).forEach(id => {
        state.editors[id].isActive = false
      })
      
      // 激活指定编辑器
      if (state.editors[noteId]) {
        state.editors[noteId].isActive = true
        state.activeNoteId = noteId
        
        // 更新最近使用记录
        state.instanceManagement.recentlyUsed = state.instanceManagement.recentlyUsed
          .filter(id => id !== noteId)
          .concat(noteId)
      }
    },
    
    // 更新编辑器内容
    updateEditorContent: (state, action: PayloadAction<{ noteId: string; content: string }>) => {
      const { noteId, content } = action.payload
      
      if (state.editors[noteId]) {
        state.editors[noteId].content = content
        state.editors[noteId].isEditing = true
        state.editors[noteId].lastSavedAt = null
      }
    },
    
    // 设置编辑器实例
    setEditorInstance: (state, action: PayloadAction<{ noteId: string; editor: any }>) => {
      const { noteId, editor } = action.payload
      
      if (state.editors[noteId]) {
        state.editors[noteId].editorInstance = editor
        state.editorInstances[noteId] = editor
        state.instanceManagement.instanceLifetimes[noteId] = Date.now()
      }
    },
    
    // 设置编辑器焦点状态
    setEditorFocus: (state, action: PayloadAction<{ noteId: string; isFocused: boolean }>) => {
      const { noteId, isFocused } = action.payload
      
      if (state.editors[noteId]) {
        state.editors[noteId].editorConfig.isFocused = isFocused
      }
    },
    
    // 保存编辑器滚动位置
    saveScrollPosition: (state, action: PayloadAction<{ noteId: string; scrollPosition: number }>) => {
      const { noteId, scrollPosition } = action.payload
      
      if (state.editors[noteId]) {
        state.editors[noteId].editorConfig.scrollPosition = scrollPosition
      }
    },
    
    // 保存编辑器视口状态
    saveViewportState: (state, action: PayloadAction<{ noteId: string; viewportState: any }>) => {
      const { noteId, viewportState } = action.payload
      
      if (state.editors[noteId]) {
        state.editors[noteId].editorConfig.viewportState = viewportState
      }
    },
    
    // 开始保存
    startSaving: (state, action: PayloadAction<string>) => {
      const noteId = action.payload
      
      if (state.editors[noteId]) {
        state.editors[noteId].isSaving = true
      }
    },
    
    // 保存完成
    saveComplete: (state, action: PayloadAction<{ noteId: string; content?: string }>) => {
      const { noteId, content } = action.payload
      
      if (state.editors[noteId]) {
        state.editors[noteId].isSaving = false
        state.editors[noteId].isEditing = false
        state.editors[noteId].lastSavedAt = Date.now()
        
        if (content) {
          state.editors[noteId].content = content
        }
      }
    },
    
    // 更新选择状态
    updateSelection: (state, action: PayloadAction<{ noteId: string; selection: { from: number; to: number } | null }>) => {
      const { noteId, selection } = action.payload
      
      if (state.editors[noteId]) {
        state.editors[noteId].selection = selection
      }
    },
    
    // 设置自动保存配置
    setAutoSaveConfig: (state, action: PayloadAction<{ enabled: boolean; interval?: number }>) => {
      const { enabled, interval } = action.payload
      
      state.autoSave.enabled = enabled
      if (interval !== undefined) {
        state.autoSave.interval = interval
      }
    },
    
    // 设置最大实例数
    setMaxInstances: (state, action: PayloadAction<number>) => {
      state.instanceManagement.maxInstances = action.payload
    },
    
    // 清理不活跃的编辑器实例
    cleanupInactiveInstances: (state) => {
      const now = Date.now()
      const inactiveThreshold = 10 * 60 * 1000 // 10分钟不活跃
      
      Object.keys(state.editors).forEach(noteId => {
        const lastActive = state.instanceManagement.instanceLifetimes[noteId]
        const isActive = state.editors[noteId].isActive
        
        // 清理不活跃且长时间未使用的实例
        if (!isActive && lastActive && now - lastActive > inactiveThreshold) {
          delete state.editors[noteId]
          delete state.editorInstances[noteId]
          delete state.instanceManagement.instanceLifetimes[noteId]
          state.instanceManagement.recentlyUsed = state.instanceManagement.recentlyUsed
            .filter(id => id !== noteId)
          
          // 如果清理的是当前激活的编辑器，更新激活状态
          if (state.activeNoteId === noteId) {
            const remainingNoteIds = Object.keys(state.editors)
            if (remainingNoteIds.length > 0) {
              state.activeNoteId = remainingNoteIds[remainingNoteIds.length - 1]
              state.editors[state.activeNoteId].isActive = true
            } else {
              state.activeNoteId = null
            }
          }
        }
      })
    },
    
    // 批量关闭所有编辑器
    closeAllEditors: (state) => {
      state.editors = {}
      state.editorInstances = {}
      state.activeNoteId = null
      state.instanceManagement.instanceLifetimes = {}
      state.instanceManagement.recentlyUsed = []
    },
    
    // 关闭除指定笔记外的所有编辑器
    closeOtherEditors: (state, action: PayloadAction<string>) => {
      const keepNoteId = action.payload
      const editorsToKeep: Record<string, NoteEditorState> = {}
      const instancesToKeep: Record<string, any> = {}
      const lifetimesToKeep: Record<string, number> = {}
      
      if (state.editors[keepNoteId]) {
        editorsToKeep[keepNoteId] = state.editors[keepNoteId]
        instancesToKeep[keepNoteId] = state.editorInstances[keepNoteId]
        lifetimesToKeep[keepNoteId] = state.instanceManagement.instanceLifetimes[keepNoteId]
      }
      
      state.editors = editorsToKeep
      state.editorInstances = instancesToKeep
      state.instanceManagement.instanceLifetimes = lifetimesToKeep
      state.instanceManagement.recentlyUsed = [keepNoteId]
      state.activeNoteId = keepNoteId
    },
    
    // 恢复编辑器状态（用于页面刷新或重新加载）
    restoreEditorState: (state, action: PayloadAction<{ noteId: string; state: Partial<NoteEditorState> }>) => {
      const { noteId, state: editorState } = action.payload
      
      if (state.editors[noteId]) {
        state.editors[noteId] = {
          ...state.editors[noteId],
          ...editorState
        }
      }
    },
    
    // 从外部更新编辑器内容（例如，当note数据通过其他途径更新时）
    externalUpdateEditorContent: (state, action: PayloadAction<{ noteId: string; content: string }>) => {
      const { noteId, content } = action.payload
      
      if (state.editors[noteId]) {
        // 只有当内容真的变化时才更新，避免不必要的重渲染
        if (state.editors[noteId].content !== content) {
          state.editors[noteId].content = content
          // 不设置isEditing为true，因为这是外部更新，不是用户编辑
        }
      }
    },
    
    // 更新编辑器标题（当笔记标题从外部更新时使用）
    updateEditorTitle: (state, action: PayloadAction<{ noteId: string; title: string }>) => {
      const { noteId, title } = action.payload
      
      if (state.editors[noteId]) {
        state.editors[noteId].title = title
      }
    },
    
    // 设置布局模式
    setLayoutMode: (state, action: PayloadAction<'tab' | 'split-horizontal' | 'split-vertical' | 'grid'>) => {
      state.layoutMode = action.payload
    },
    
    // 开始加载状态
    startLoadingState: (state) => {
      state.stateTransition.isLoadingState = true
    },
    
    // 完成加载状态
    finishLoadingState: (state) => {
      state.stateTransition.isLoadingState = false
      state.stateTransition.lastLoadedAt = Date.now()
    },
    
    // 开始保存状态
    startSavingState: (state) => {
      state.stateTransition.isSavingState = true
    },
    
    // 完成保存状态
    finishSavingState: (state) => {
      state.stateTransition.isSavingState = false
      state.stateTransition.lastSavedAt = Date.now()
    }
  },
  extraReducers: (builder) => {
    builder
      // 监听笔记更新事件，同步更新编辑器标题
      .addCase(updateNote.fulfilled, (state, action) => {
        const { id: noteId, title } = action.payload
        
        // 如果该笔记在编辑器中打开，更新其标题
        if (state.editors[noteId]) {
          state.editors[noteId].title = title
        }
      })
  }
})

export const {
  openNoteEditor,
  closeNoteEditor,
  activateNoteEditor,
  updateEditorContent,
  externalUpdateEditorContent,
  updateEditorTitle,
  setEditorInstance,
  setEditorFocus,
  saveScrollPosition,
  saveViewportState,
  startSaving,
  saveComplete,
  updateSelection,
  setAutoSaveConfig,
  setMaxInstances,
  cleanupInactiveInstances,
  closeAllEditors,
  closeOtherEditors,
  restoreEditorState,
  setLayoutMode,
  startLoadingState,
  finishLoadingState,
  startSavingState,
  finishSavingState
} = standardNoteEditorSlice.actions

export default standardNoteEditorSlice.reducer
